import { Link } from 'react-router-dom';
import { ROUTES } from '@/app/routes';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { safeMediaSrc, isRasterMediaSrc } from '@/lib/mediaUrl';
import { ENGAGEMENT_LOCATION_HELP, contentTypeMeta } from '../lib/engagementColors';
import { SectionLocationHint } from './EngagementChartFilters';
import { EngagementBusy } from './EngagementNotReady';

export type TopContentView = {
  postId: number;
  title: string;
  contentType?: string;
  contentTypeLabel?: string;
  creatorDisplayName?: string;
  creatorAvatarUrl?: string | null;
  thumbnailUrl?: string | null;
  views?: number | null;
  purchaseCount?: number | null;
};

const compact = new Intl.NumberFormat(undefined, {
  notation: 'compact',
  maximumFractionDigits: 1,
});

export function TopPaidPostsCard({ items, isLoading }: { items: TopContentView[]; isLoading?: boolean }) {
  return (
    <TopList
      title="Top Paid Posts"
      infoHint={ENGAGEMENT_LOCATION_HELP.connectCountry}
      titleClassName="text-sky-400"
      hoverClassName="hover:border-sky-400/50"
      emptyLabel="No paid posts in this window."
      items={items}
      metricLabel="Purchases"
      metricValue={(item) => compact.format(item.purchaseCount ?? 0)}
      {...(isLoading != null ? { isLoading } : {})}
    />
  );
}

export function TopPerformingContentCard({
  items,
  isLoading,
}: {
  items: TopContentView[];
  isLoading?: boolean;
}) {
  return (
    <TopList
      title="Top Performing Content"
      infoHint={ENGAGEMENT_LOCATION_HELP.topPerforming}
      titleClassName="text-violet-400"
      hoverClassName="hover:border-violet-400/50"
      emptyLabel="No viewed content in this window."
      items={items}
      metricLabel="Views"
      metricValue={(item) => compact.format(item.views ?? 0)}
      {...(isLoading != null ? { isLoading } : {})}
    />
  );
}

/**
 * Figma ranking rail: thumbnail, title + type, a lined-up metric column, creator.
 * Shared grid tracks keep purchases/views and names aligned across rows.
 */
function TopList({
  title,
  infoHint,
  titleClassName,
  hoverClassName,
  items,
  emptyLabel,
  metricLabel,
  metricValue,
  isLoading,
}: {
  title: string;
  infoHint: string;
  titleClassName: string;
  hoverClassName: string;
  items: TopContentView[];
  emptyLabel: string;
  metricLabel: string;
  metricValue: (item: TopContentView) => string;
  isLoading?: boolean;
}) {
  return (
    <section className="space-y-2">
      <div className="flex items-center gap-2">
        <h2 className={`text-sm font-semibold ${titleClassName}`}>{title}</h2>
        <SectionLocationHint
          text={infoHint}
          label={`How ${title} uses the location filter. ${infoHint}`}
        />
      </div>
      {isLoading ? (
        <EngagementBusy className="h-32 rounded-xl border border-border/40 bg-[#141416]" />
      ) : items.length === 0 ? (
        <p className="rounded-xl border border-border/40 bg-[#141416] px-4 py-6 text-center text-xs text-muted-foreground">
          {emptyLabel}
        </p>
      ) : (
        <ul className="space-y-2">
          {items.map((item) => {
            const { icon: Icon, color } = contentTypeMeta(item.contentType);
            const initial = (item.creatorDisplayName ?? '?').charAt(0).toUpperCase();
            const thumbSrc = safeMediaSrc(item.thumbnailUrl);
            const thumbIsRaster = isRasterMediaSrc(item.thumbnailUrl);
            const avatarSrc = safeMediaSrc(item.creatorAvatarUrl);
            const creator = item.creatorDisplayName ?? '—';
            return (
              <li key={item.postId}>
                <Link
                  to={ROUTES.contentDetails(String(item.postId))}
                  className={`grid grid-cols-[3.25rem_minmax(0,1fr)_4.5rem_3.75rem] items-center gap-x-3 rounded-xl border border-border/40 bg-[#141416] px-2.5 py-2 transition-colors ${hoverClassName}`}
                >
                  {thumbIsRaster ? (
                    <img
                      src={thumbSrc}
                      alt=""
                      className="h-[3.25rem] w-[3.25rem] rounded-lg object-cover"
                    />
                  ) : thumbSrc ? (
                    <video
                      src={thumbSrc}
                      className="h-[3.25rem] w-[3.25rem] rounded-lg object-cover"
                      muted
                      playsInline
                      preload="metadata"
                    />
                  ) : (
                    <span
                      className="flex h-[3.25rem] w-[3.25rem] items-center justify-center rounded-lg"
                      style={{ backgroundColor: `${color}1f` }}
                    >
                      <Icon className="size-5" style={{ color }} aria-hidden />
                    </span>
                  )}

                  <span className="min-w-0">
                    <span className="block truncate text-sm font-medium leading-5">{item.title}</span>
                    {item.contentTypeLabel ? (
                      <span className="mt-0.5 block truncate text-xs font-medium" style={{ color }}>
                        {item.contentTypeLabel}
                      </span>
                    ) : null}
                  </span>

                  <span className="text-right">
                    <span className="block text-sm font-bold tabular-nums leading-5">
                      {metricValue(item)}
                    </span>
                    <span className="mt-0.5 block text-[11px] leading-4 text-muted-foreground">
                      {metricLabel}
                    </span>
                  </span>

                  <span className="flex min-w-0 flex-col items-center gap-1">
                    <Avatar className="size-8">
                      {avatarSrc ? <AvatarImage src={avatarSrc} alt="" /> : null}
                      <AvatarFallback className="text-[10px]">{initial}</AvatarFallback>
                    </Avatar>
                    <span
                      className="w-full truncate text-center text-[11px] leading-4"
                      title={creator}
                    >
                      {creator}
                    </span>
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
